import classNames from 'classnames';
import type { PropsWithChildren } from 'react';
import { useId } from 'react';
import type { CSSProperties } from 'styled-components';
import styled, { keyframes } from 'styled-components';

import type { RGBColor } from 'style/types';

const glitch = keyframes`
  0% {
    filter: none;
  }
  10% {
    filter: var(--low-filter-id);
  }
  25% {
    filter: var(--medium-filter-id);
  }
  40% {
    filter: var(--hard-negative-filter-id);
  }
  50% {
    filter: var(--hard-filter-id);
  }
  60% {
    filter: var(--hard-negative-filter-id);
  }
  75% {
    filter: var(--medium-filter-id);
  }
  90% {
    filter: var(--low-filter-id);
  }
  90% {
    filter: none;
  }
`;
const fadeInOut = keyframes`
  0% {
    opacity: 0;
  }
  30% {
    opacity: 1;
  }
  70% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const Root = styled.div`
  position: relative;
`;
const Wrapper = styled.div`
  --bg-color: var(--c-limited);
  position: relative;

  .glitching & {
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: radial-gradient(
        60% 60% at 50% 50%,
        rgb(var(--bg-rgb-color)),
        transparent
      );
      opacity: 0;
      animation: 1000ms ${fadeInOut} ease-in-out forwards;
    }

    animation: 600ms ${glitch} linear;
  }
`;

type SvgFilterDefProps = {
  lowId: string;
  mediumId: string;
  hardId: string;
  hardNegativeId: string;
};
const SvgFilterDef = ({
  lowId,
  mediumId,
  hardId,
  hardNegativeId,
}: SvgFilterDefProps) => {
  return (
    <svg width={0} height={0} style={{ display: 'none' }}>
      <filter id={lowId}>
        <feTurbulence
          type="turbulence"
          baseFrequency="0.0001 0.15"
          numOctaves="3"
          seed="2"
          result="noise"
        />
        <feDisplacementMap
          in="SourceGraphic"
          in2="noise"
          scale={2}
          xChannelSelector="R"
          yChannelSelector="G"
        />
      </filter>
      <filter id={mediumId}>
        <feTurbulence
          type="turbulence"
          baseFrequency="0.0001 0.15"
          numOctaves="3"
          seed="2"
          result="noise"
        />
        <feDisplacementMap
          in="SourceGraphic"
          in2="noise"
          scale={4}
          xChannelSelector="R"
          yChannelSelector="G"
        />
      </filter>
      <filter id={hardId}>
        <feTurbulence
          type="turbulence"
          baseFrequency="0.0001 0.15"
          numOctaves="3"
          seed="2"
          result="noise"
        />
        <feDisplacementMap
          in="SourceGraphic"
          in2="noise"
          scale={25}
          xChannelSelector="R"
          yChannelSelector="G"
        />
      </filter>
      <filter id={hardNegativeId}>
        <feTurbulence
          type="turbulence"
          baseFrequency="0.0001 0.15"
          numOctaves="3"
          seed="2"
          result="noise"
        />
        <feDisplacementMap
          in="SourceGraphic"
          in2="noise"
          scale={-25}
          xChannelSelector="R"
          yChannelSelector="G"
        />
      </filter>
    </svg>
  );
};

type Props = { glitching?: boolean; bgRgbColor?: RGBColor | null };
export const HorizontalGlitch = ({
  children,
  bgRgbColor,
  glitching,
}: PropsWithChildren<Props>) => {
  const lowId = useId();
  const mediumId = useId();
  const hardId = useId();
  const hardNegativeId = useId();

  return (
    <Root
      className={classNames({
        glitching,
      })}
      style={
        {
          '--low-filter-id': `url(#${lowId})`,
          '--medium-filter-id': `url(#${mediumId})`,
          '--hard-filter-id': `url(#${hardId})`,
          '--hard-negative-filter-id': `url(#${hardNegativeId})`,
          '--bg-rgb-color': bgRgbColor,
        } as CSSProperties
      }
    >
      <SvgFilterDef
        lowId={lowId}
        mediumId={mediumId}
        hardId={hardId}
        hardNegativeId={hardNegativeId}
      />
      <Wrapper>{children}</Wrapper>
    </Root>
  );
};
